import React, { useCallback } from 'react';
import { Button } from 'tuui';
import { DropdownButton, DropdownMenu, STYLED_BUTTON_SIZE, StyledButton, withStylesheet } from 'taboola-ultimate-ui';
import { useDropdownOptions, useModuleName, useNavigate } from 'hooks';
import useAvailableItems from 'hooks/useAvailableItems';
import styles from 'modules/campaigns/modules/campaigns-reports/components/GridToolbar/gridToolbar.module.scss';
import {
    FEATURE_FLAGS,
    useAccount,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import {
    generateConversionCreatorPath,
    generateConversionFunnelCreatorPath,
} from 'modules/tracking/utils/generateTrackingPath';
import { useTrackingSetupConversionTypeModal } from '../../hooks/useTrackingSetupConversionTypeModal';

const StyledAddButton = withStylesheet(StyledButton, styles, {
    [StyledButton.classNames.iconBefore]: 'dropdown-button-icon',
});

const optionsConfigurations = [
    {
        id: 'conversion',
        value: 'conversion',
        label: 'Conversion',
        path: generateConversionCreatorPath(),
        dataMetricColumnName: 'Conversion',
    },
    {
        id: 'conversion-funnel',
        value: 'conversion-funnel',
        label: 'Conversion Funnel',
        path: generateConversionFunnelCreatorPath(),
        dataMetricColumnName: 'Conversion Funnel',
        permissions: ['TA_CONVERSION_FUNNEL'],
        accountConfigurations: { [FEATURE_FLAGS.CONVERSION_FUNNEL_ENABLED]: 'true' },
    },
];

export const CreateConversionButton = () => {
    const moduleName = useModuleName();
    const navigate = useNavigate();
    const selectedAccount = useAccount();

    const rawOptions = useAvailableItems(optionsConfigurations, {
        selectedAccount,
        moduleName,
    });

    const options = useDropdownOptions(rawOptions, 'app.gridToolbar');

    const handleOnChange = useCallback(
        ({ path }) => {
            navigate(path);
        },
        [navigate]
    );
    const isCodelessEnabled = useConfigMatch({
        [FEATURE_FLAGS.CODELESS_CONVERSIONS_ENABLED]: 'true',
    });
    const isNewTrackingScreenPermitted = usePermissions(['REALIZE_NEW_TRACKING_SCREEN']);
    const { openTrackingSetupModal } = useTrackingSetupConversionTypeModal();
    const canUseCodelessTrackingScreen = isCodelessEnabled && isNewTrackingScreenPermitted;

    return canUseCodelessTrackingScreen ? (
        <Button
            className="tracking-setup-conversion"
            size={Button.size.md}
            data-metrics-event-name={GTM_EVENTS.USABILITY}
            data-metrics-component="TrackingSetupButton"
            onClick={openTrackingSetupModal}
        >
            <FormattedMessage id="app.gridToolbar.create.conversion" defaultMessage="+ New Conversion" />
        </Button>
    ) : (
        <div
            data-metrics-event-name={GTM_EVENTS.USABILITY}
            data-metrics-component="CreateButton"
            data-automation="CreateButton"
        >
            {rawOptions?.length === 1 ? (
                <Button onClick={() => handleOnChange(rawOptions[0])} data-metrics-event-name={GTM_EVENTS.USABILITY}>
                    <FormattedMessage id="app.gridToolbar.create.conversion" defaultMessage="+ New Conversion" />
                </Button>
            ) : (
                <DropdownButton
                    ButtonComponent={StyledAddButton}
                    buttonText={<FormattedMessage id="app.gridToolbar.create" defaultMessage="Create" />}
                    onChange={handleOnChange}
                    buttonSize={STYLED_BUTTON_SIZE.MEDIUM}
                    options={options}
                    shouldStretchedByContent
                    strechedMenuPosition={DropdownMenu.MENU_POSITION.RIGHT}
                />
            )}
        </div>
    );
};
